import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
  Card,
  CircularProgress,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Box
} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {GET} from "APIclient";
import properties from "constants/ApplicationConstants";
import DescriptionIcon from "@mui/icons-material/Description";

function KRAList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({open: false, message: "", type: ""});
  const [employeeList, setEmployeeList] = useState([]);
  const [filteredEmployeeList, setFilteredEmployeeList] = useState([]);
  const [managerId, setManagerId] = useState(null);
  const [appraisalId, setAppraisalId] = useState(null);

  const [searchEmpId, setSearchEmpId] = useState("");
  const [searchName, setSearchName] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeGoals, setEmployeeGoals] = useState(null);
  const [loadingGoals, setLoadingGoals] = useState(false);

  useEffect(() => {
    const empId = localStorage.getItem("empId") || sessionStorage.getItem("empId");
    if (empId) {
      const parsedEmpId = parseInt(empId);
      setManagerId(parsedEmpId);
      initializeData(parsedEmpId);
    } else {
      showNotification("Manager ID not found", "error");
      setLoading(false);
    }
  }, []);

  const initializeData = async empId => {
    try {
      setLoading(true);
      await fetchAppraisalPermission(empId);
    } catch (error) {
      showNotification("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAppraisalPermission = async managerId => {
    try {
      const response = await GET(`manager/appraisal/current`);
      if (response.data?.data) {
        const currentAppraisalId = response.data.data.appraisalId;
        setAppraisalId(currentAppraisalId);
        await fetchEmployeesWithKRA(currentAppraisalId, managerId);
      }
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to load appraisal details", "error");
    }
  };

  const fetchEmployeesWithKRA = async (appraisalId, managerId) => {
    try {
      const response = await GET(`manager/reportees/${managerId}`);
      if (response.data?.data) {
        const employeesData = response.data.data.paginatedData || response.data.data;
        const employees = Array.isArray(employeesData)
          ? employeesData.map(emp => ({
              id: emp.empId,
              empId: emp.empId,
              firstName: emp.firstName || emp.first_name || "",
              middleName: emp.middleName || emp.middle_name || "",
              lastName: emp.lastName || emp.last_name || "",
              fullName: `${emp.firstName || emp.first_name || ""} ${emp.lastName || emp.last_name || ""}`.trim(),
              officialEmailAddress: emp.officialEmailAddress || emp.official_email_address || "",
              designation: emp.designation || "",
              department: emp.department || "",
              applicableSkills: emp.applicableSkills || null,
              skillCount: emp.applicableSkills ? emp.applicableSkills.length : 0
            }))
          : [];

        const employeesWithKRA = employees.filter(
          emp => emp.applicableSkills && emp.applicableSkills.length > 0
        );
        setEmployeeList(employeesWithKRA);
        setFilteredEmployeeList(employeesWithKRA);
        if (employeesWithKRA.length === 0) showNotification("No employees have KRA assigned yet", "info");
      }
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to load employees", "error");
    }
  };

  const handleSearch = () => {
    let filtered = employeeList;
    if (searchEmpId.trim())
      filtered = filtered.filter(emp => emp.empId.toString().includes(searchEmpId.trim()));
    if (searchName.trim())
      filtered = filtered.filter(emp =>
        emp.fullName.toLowerCase().includes(searchName.trim().toLowerCase())
      );
    setFilteredEmployeeList(filtered);
    showNotification(
      filtered.length === 0 ? "No matching records found" : `Found ${filtered.length} record(s)`,
      filtered.length === 0 ? "info" : "success"
    );
  };

  const handleReset = () => {
    setSearchEmpId("");
    setSearchName("");
    setFilteredEmployeeList(employeeList);
  };

  const handleViewGoals = async employee => {
    setSelectedEmployee(employee);
    setOpenDialog(true);
    setLoadingGoals(true);
    try {
      const response = await GET(`manager/${appraisalId}/employee/${employee.empId}/goals`);
      if (response.data?.data) setEmployeeGoals(response.data.data);
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to load employee goals", "error");
      setOpenDialog(false);
    } finally {
      setLoadingGoals(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedEmployee(null);
    setEmployeeGoals(null);
  };

  const showNotification = (message, type) => {
    setNotification({open: true, message, type});
    setTimeout(() => setNotification({open: false, message: "", type: ""}), 4000);
  };

  const renderSkillSection = (title, skills) => {
    if (!skills || skills.length === 0) return null;
    return (
      <MDBox mb={1.5}>
        <MDTypography variant="subtitle2" fontWeight="medium" mb={0.5}>
          {title}
        </MDTypography>
        <Grid container spacing={0.5}>
          {skills.map((skill, index) => (
            <Grid item xs={12} key={skill.paFormId}>
              <MDBox display="flex" alignItems="center" gap={0.75}>
                <Chip label={`${index + 1}`} size="small" color="primary" sx={{minWidth: "26px", height: "20px", fontSize: "11px"}} />
                <MDTypography variant="body2" fontSize="13px">{skill.skillSet}</MDTypography>
                {skill.selected && (
                  <Chip label="Assigned" size="small" color="success" sx={{ml: 0.5, height: "20px", fontSize: "11px"}} />
                )}
              </MDBox>
            </Grid>
          ))}
        </Grid>
      </MDBox>
    );
  };

  const columns = [
    {field: "empId", headerName: "Employee ID", width: 120},
    {field: "fullName", headerName: "Employee Name", width: 220},
    {
      field: "designation",
      headerName: "Designation",
      width: 190,
      renderCell: params => params.value || "-"
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: params => (
        <MDBox display="flex" gap={0.25} justifyContent="center">
          <Tooltip title="View Goals">
            <IconButton
              color="info"
              size="small"
              onClick={() => navigate(`/manager/appraisal/goal-settings/${params.row.empId}`)}
            >
              <VisibilityIcon sx={{fontSize: 18}} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Goals">
            <IconButton
              color="primary"
              size="small"
              onClick={() => navigate(`/manager/appraisal/edit-goals/${params.row.empId}`)}
            >
              <EditIcon sx={{fontSize: 18}} />
            </IconButton>
          </Tooltip>
          <Tooltip title="View Ratings">
            <IconButton
              color="success"
              size="small"
              onClick={() => navigate(`/manager/appraisal/view-ratings/${appraisalId}/${params.row.empId}`)}
            >
              <AssessmentIcon sx={{fontSize: 18}} />
            </IconButton>
          </Tooltip>
          <Tooltip title="KRA Form Overview">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => navigate(`/manager/appraisal/kra_form_overview/${appraisalId}/${params.row.empId}`)}
            >
              <DescriptionIcon sx={{fontSize: 18}} />
            </IconButton>
          </Tooltip>
        </MDBox>
      )
    }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={32} />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={0.5} pb={1}>

        <Card>
          {/* ── Header ── */}
          <MDBox
            variant="gradient"
            sx={{background: properties.colors.bgcolorbluethem}}
            borderRadius="lg"
            coloredShadow="success"
            mx={1.5}
            mt={0.5}
            p={1}
            mb={0}
            textAlign="center"
          >
            <MDTypography variant="h6" fontWeight="500" color="white" sx={{fontFamily: properties.fontFamily}}>
              KRA Assignment List
            </MDTypography>
            <MDTypography variant="caption" color="white" opacity={0.8} sx={{fontFamily: properties.fontFamily}}>
              View employees with assigned KRAs and their goals
            </MDTypography>
          </MDBox>

          <MDBox p={1.5}>
            {employeeList.length > 0 ? (
              <>
                {/* ── Search Bar ── */}
                <Card sx={{px: 1.5, py: 1, mb: 1.5, backgroundColor: "#f8f9fa"}}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} md={3.5}>
                      <MDInput
                        fullWidth
                        type="text"
                        label="Employee ID"
                        variant="standard"
                        value={searchEmpId}
                        onChange={e => setSearchEmpId(e.target.value)}
                        onKeyPress={e => e.key === "Enter" && handleSearch()}
                        placeholder="Search by Employee ID"
                      />
                    </Grid>
                    <Grid item xs={12} md={3.5}>
                      <MDInput
                        fullWidth
                        type="text"
                        label="Employee Name"
                        variant="standard"
                        value={searchName}
                        onChange={e => setSearchName(e.target.value)}
                        onKeyPress={e => e.key === "Enter" && handleSearch()}
                        placeholder="Search by Name"
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <MDBox display="flex" gap={0.75} justifyContent="flex-end" flexWrap="wrap">
                        <MDButton
                          variant="gradient"
                          color="info"
                          size="small"
                          startIcon={<SearchIcon />}
                          onClick={handleSearch}
                          sx={{background: properties.colors.bgcolorbluethem, fontFamily: properties.fontFamily, height: "32px"}}
                        >
                          Search
                        </MDButton>
                        <MDButton
                          variant="outlined"
                          color="secondary"
                          size="small"
                          startIcon={<RefreshIcon />}
                          onClick={handleReset}
                          sx={{fontFamily: properties.fontFamily, height: "32px"}}
                        >
                          Reset
                        </MDButton>
                        <MDButton
                          variant="gradient"
                          color="success"
                          size="small"
                          onClick={() => navigate("/manager/appraisal/assign")}
                          sx={{fontFamily: properties.fontFamily, height: "32px"}}
                        >
                          Assign New KRA
                        </MDButton>
                      </MDBox>
                    </Grid>
                  </Grid>
                </Card>

                <MDBox mb={0.75}>
                  <MDTypography variant="subtitle2" fontWeight="medium">
                    Employees with KRA ({filteredEmployeeList.length})
                  </MDTypography>
                </MDBox>

                {/* ── DataGrid ── */}
                <Box sx={{width: "100%"}}>
                  <DataGrid
                    rows={filteredEmployeeList}
                    columns={columns}
                    initialState={{pagination: {paginationModel: {pageSize: 10}}}}
                    pageSizeOptions={[5, 10, 25, 50]}
                    disableRowSelectionOnClick
                    autoHeight
                    rowHeight={40}
                    columnHeaderHeight={42}
                    sx={{
                      fontFamily: properties.fontFamily,
                      fontSize: "13px",
                      "& .MuiDataGrid-row:hover": {backgroundColor: "#f5f5f5"},
                      "& .MuiDataGrid-columnHeaders": {backgroundColor: "#f5f5f5", fontWeight: "bold"},
                      "& .MuiDataGrid-cell": {py: 0}
                    }}
                  />
                </Box>
              </>
            ) : (
              <Alert severity="info" sx={{py: 1}}>
                <AlertTitle sx={{fontFamily: properties.fontFamily, mb: 0.5}}>
                  No KRA Assignments Found
                </AlertTitle>
                No employees have been assigned KRA yet.
                <MDBox mt={1}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    onClick={() => navigate("/manager/appraisal/assign")}
                    sx={{background: properties.colors.bgcolorbluethem, fontFamily: properties.fontFamily}}
                  >
                    Assign KRA Now
                  </MDButton>
                </MDBox>
              </Alert>
            )}
          </MDBox>
        </Card>

        {/* ── Goals Dialog ── */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle sx={{py: 1, px: 2}}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6" fontWeight="medium">
                Employee Goals — {selectedEmployee?.fullName}
              </MDTypography>
              <IconButton onClick={handleCloseDialog} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </MDBox>
          </DialogTitle>

          <DialogContent dividers sx={{p: 1.5}}>
            {loadingGoals ? (
              <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={28} />
              </MDBox>
            ) : employeeGoals ? (
              <MDBox>
                {selectedEmployee && (
                  <Alert severity="info" sx={{mb: 1.5, py: 0.5}}>
                    <MDTypography variant="caption" display="block"><strong>Employee ID:</strong> {selectedEmployee.empId}</MDTypography>
                    <MDTypography variant="caption" display="block"><strong>Email:</strong> {selectedEmployee.officialEmailAddress}</MDTypography>
                    <MDTypography variant="caption" display="block"><strong>Designation:</strong> {selectedEmployee.designation || "-"}</MDTypography>
                    <MDTypography variant="caption" display="block"><strong>Department:</strong> {selectedEmployee.department || "-"}</MDTypography>
                  </Alert>
                )}

                {renderSkillSection("Performance Competency Primary Skills", employeeGoals.competencyPrimary)}
                {employeeGoals.competencyPrimary?.length > 0 && <Divider sx={{my: 1}} />}

                {renderSkillSection("Performance Competency Secondary Skills", employeeGoals.competencySecondary)}
                {employeeGoals.competencySecondary?.length > 0 && <Divider sx={{my: 1}} />}

                {renderSkillSection("Technical Secondary Skills", employeeGoals.technicalSecondary)}
                {employeeGoals.technicalSecondary?.length > 0 && <Divider sx={{my: 1}} />}

                {renderSkillSection("Technical Primary Skills (Assigned)", employeeGoals.technicalPrimary)}

                {!employeeGoals.competencyPrimary?.length &&
                  !employeeGoals.competencySecondary?.length &&
                  !employeeGoals.technicalSecondary?.length &&
                  !employeeGoals.technicalPrimary?.length && (
                    <Alert severity="warning" sx={{py: 0.5}}>No goals found for this employee.</Alert>
                  )}
              </MDBox>
            ) : (
              <Alert severity="error" sx={{py: 0.5}}>Failed to load employee goals.</Alert>
            )}
          </DialogContent>

          <DialogActions sx={{py: 0.75, px: 1.5}}>
            <MDButton variant="outlined" color="dark" size="small" onClick={handleCloseDialog}>
              Close
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* ── Notification ── */}
        {notification.open && (
          <MDBox sx={{position: "fixed", top: 16, right: 16, zIndex: 9999, minWidth: "280px"}}>
            <Alert
              severity={notification.type}
              onClose={() => setNotification({open: false, message: "", type: ""})}
              sx={{py: 0.5}}
            >
              {notification.message}
            </Alert>
          </MDBox>
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default KRAList;